import { PackageOpen, RefreshCcw, ShieldCheck, Clock, XCircle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function ReturnPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="return-policy-page">
      <div className="mb-10">
        <span className="badge-sage inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Customer Policy</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-bold text-[#1A201C] tracking-tight">Return, Refund & Replacement Policy</h1>
        <p className="mt-3 text-[#5A6B5D] max-w-3xl">
          Healthion India, operated by <strong>Amiroop Healthion LLP</strong>, is committed to protecting every customer.
          This policy applies to all products sold through the platform by our verified vendors.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <PolicyCard icon={Clock} title="7-Day Window" body="Raise a return within 7 days of delivery for eligible products." />
        <PolicyCard icon={RefreshCcw} title="Free Reverse Pickup" body="For damaged, wrong or expired items, we cover reverse logistics." />
        <PolicyCard icon={CheckCircle2} title="Refunds in 5–7 Days" body="Refunds credited to original payment source within 5–7 working days of pickup." />
      </div>

      <article className="bg-white border border-[#E2DDD5] rounded-3xl p-8 sm:p-10 space-y-6 text-[#1A201C] leading-relaxed">
        <Section n="1" icon={CheckCircle2} title="Eligible for return">
          <ul className="list-disc pl-6 space-y-1.5 text-[15px]">
            <li>Product received is damaged, tampered with, or arrives broken.</li>
            <li>Product delivered is different from what was ordered (wrong SKU, flavour, size).</li>
            <li>Product delivered is expired or has less than 3 months of shelf life remaining.</li>
            <li>Unopened, sealed products in original packaging within 7 days of delivery.</li>
          </ul>
        </Section>

        <Section n="2" icon={XCircle} title="Not eligible for return">
          <ul className="list-disc pl-6 space-y-1.5 text-[15px]">
            <li>Opened, used, or partially consumed consumables (oils, powders, capsules, teas).</li>
            <li>Personal care items where the safety seal has been broken.</li>
            <li>Products that show signs of misuse, mishandling or storage in extreme conditions.</li>
            <li>Return requests raised after the 7-day window from delivery.</li>
            <li>Free gifts, promotional items, or products purchased under flash-sale "no return" tags.</li>
          </ul>
        </Section>

        <Section n="3" icon={PackageOpen} title="How to raise a return">
          <ol className="list-decimal pl-6 space-y-1.5 text-[15px]">
            <li>Go to <strong>My Account → Order History</strong> and select the order.</li>
            <li>Tap <em>"Request Return"</em> and select the reason, upload a clear photo of the product and packaging.</li>
            <li>Our team validates the request within 24 hours and schedules a free reverse pickup (for eligible cases).</li>
            <li>Once the vendor receives the product and verifies its condition, your refund/replacement is processed.</li>
          </ol>
        </Section>

        <Section n="4" icon={RefreshCcw} title="Refund timelines">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#1E3A2B] text-[#FAF8F5]">
                  <th className="text-left p-3 font-serif font-semibold">Payment Method</th>
                  <th className="text-left p-3 font-serif font-semibold">Refund Timeline (from pickup verification)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#E2DDD5]"><td className="p-3">UPI</td><td className="p-3">Within 3–5 business days</td></tr>
                <tr className="border-b border-[#E2DDD5] bg-[#FAF8F5]"><td className="p-3">Credit / Debit Card</td><td className="p-3">Within 5–7 business days</td></tr>
                <tr className="border-b border-[#E2DDD5]"><td className="p-3">Net Banking</td><td className="p-3">Within 5–7 business days</td></tr>
                <tr className="bg-[#FAF8F5]"><td className="p-3">Cash on Delivery</td><td className="p-3">Refund via UPI/Bank transfer within 7 business days</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section n="5" icon={ShieldCheck} title="Vendor accountability">
          <p className="text-[15px]">
            As per our <Link to="/vendor/agreement" className="text-[#1E3A2B] font-semibold underline">Marketplace Vendor Agreement</Link>,
            vendors bear the full cost of replacement/refund and reverse logistics for damaged, wrong or expired products.
            Amiroop Healthion LLP acts as the trusted intermediary to guarantee your rights.
          </p>
        </Section>

        <div className="bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-xl p-5">
          <div className="text-xs font-mono uppercase text-[#1E3A2B]">Contact for return support</div>
          <p className="text-sm mt-2">
            Message us on <strong>WhatsApp</strong> using the chat bubble on this site, call us at <strong>+91 70808 00384</strong>, or email <strong>care@healthioindia.com</strong>.
            Include your Order ID and one clear photo for faster resolution.
          </p>
        </div>
      </article>
    </div>
  );
}

function PolicyCard({ icon: Icon, title, body }) {
  return (
    <div className="card-warm p-5">
      <Icon className="w-6 h-6 text-[#D97706]" />
      <div className="mt-3 font-serif text-xl font-semibold text-[#1A201C]">{title}</div>
      <div className="mt-1 text-sm text-[#5A6B5D]">{body}</div>
    </div>
  );
}

function Section({ n, icon: Icon, title, children }) {
  return (
    <section>
      <h2 className="flex items-center gap-3 font-serif text-2xl font-bold text-[#1E3A2B]">
        <span className="font-mono text-sm text-[#D97706]">{n}.</span>
        <Icon className="w-5 h-5 text-[#1E3A2B]" />
        {title}
      </h2>
      <div className="mt-3 space-y-2">{children}</div>
    </section>
  );
}
